/**
 * CheckoutPage
 * Review order, enter details, and complete purchase
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  TextField,
  AppBar,
  Toolbar,
  Button,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  CreditCard as CheckoutIcon,
} from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';
import { OrderSummary, CheckoutButton, CartBadge } from '../components/commerce';
import { useCartStore, useCartItemsWithDetails } from '../store/cart';
import { trackPageView } from '../lib/analytics';

const CheckoutPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const items = useCartItemsWithDetails();

  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [emailError, setEmailError] = useState('');

  // Track page view on mount
  useEffect(() => {
    trackPageView('checkout');
  }, []);

  // Redirect if cart is empty
  useEffect(() => {
    if (items.length === 0) {
      navigate('/pricing');
    }
  }, [items.length, navigate]);

  const validateEmail = (value: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!value) {
      setEmailError('Email is required');
      return false;
    }
    if (!emailRegex.test(value)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (value) {
      validateEmail(value);
    } else {
      setEmailError('');
    }
  };

  const isFormValid = email && !emailError && termsAccepted;

  if (items.length === 0) {
    return null; // Will redirect
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar>
          <Typography
            variant="h6"
            component={Link}
            to="/"
            sx={{
              flexGrow: 1,
              textDecoration: 'none',
              color: 'primary.main',
              fontWeight: 'bold',
            }}
          >
            REDEX
          </Typography>

          <Button
            component={Link}
            to="/cart"
            startIcon={<BackIcon />}
            sx={{ mr: 2 }}
          >
            Back to Cart
          </Button>

          <CartBadge onClick={() => navigate('/cart')} />
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
          <CheckoutIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight="bold">
            Checkout
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Left Column - Contact Info */}
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 4 }}>
              <Typography variant="h6" gutterBottom>
                Contact Information
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                We'll send your receipt and project updates to this email
              </Typography>

              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={handleEmailChange}
                error={!!emailError}
                helperText={emailError}
                required
                sx={{ mb: 3 }}
              />

              <TextField
                fullWidth
                label="Company Name (Optional)"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                sx={{ mb: 3 }}
              />

              <Typography variant="body2" color="text.secondary">
                Billing address and payment details will be collected securely
                on the next step via Stripe.
              </Typography>
            </Paper>
          </Grid>

          {/* Right Column - Order Summary & Checkout */}
          <Grid item xs={12} md={5}>
            <OrderSummary
              termsAccepted={termsAccepted}
              onTermsChange={setTermsAccepted}
              email={email}
              company={company}
            />

            <Box sx={{ mt: 3 }}>
              <CheckoutButton
                disabled={!isFormValid}
                email={email}
                company={company}
              />

              {!termsAccepted && email && !emailError && (
                <Typography
                  variant="body2"
                  color="error"
                  textAlign="center"
                  sx={{ mt: 2 }}
                >
                  Please accept the Terms and Privacy Policy to continue
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default CheckoutPage;
